import React, { useState, useEffect } from 'react';
import { IonApp, IonRouterOutlet, IonSpinner, IonSplitPane, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import { CapacitorHttp } from '@capacitor/core';
import Menu from './components/Menu';
import Page from './pages/Page';
import Login from './pages/Login';
import { LoginData } from './Utils';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

setupIonicReact();

const configEndpoint = "https://api.browsedns.net/api/config";

const dummyLoginData = {
  uid: -1,
  username: "test",
  status: "online",
  postcount: 0,
  picture: "",
  userslug: "test",
  reputation: 0,
  lastonline: 0,
  displayname: "Test",
  customFields: [],
  wrappedCookies: "",
  csrfToken: ""
};

const App: React.FC = () => {

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loginData, setLoginData] = useState<LoginData>(dummyLoginData);

  useEffect(() => {

    // check if local storage has login session data
    const sessionData = localStorage.getItem("loginData");
    const sessionTime = localStorage.getItem("loginTime");
    const sessionHeaders = localStorage.getItem("loginHeaders");

    if (sessionData && sessionTime && sessionHeaders) {
      // we need to validate the session data we loaded, and pull a new csrf token
      // so while that's happening, we'll show the user a loading screen
      setIsLoading(true);

      (async () => {
        try {
          // store our wrapped cookies to attach to future requests
          const parsedSessionData = JSON.parse(sessionData);
          const parsedHeaders = JSON.parse(sessionHeaders);
          let { "x-set-cookie": wrappedCookies } = parsedHeaders
          const { "X-Set-Cookie": wrappedCookies2 } = parsedHeaders;

          if (wrappedCookies2) {
            // handle different case sensitivity for the cookie header
            wrappedCookies = wrappedCookies2;
          }

          if (!wrappedCookies) {
            alert("No session token cookies found. Please log in again.");
            localStorage.removeItem("loginData");
            localStorage.removeItem("loginTime");
            localStorage.removeItem("loginHeaders");
            location.href = "/";
            return;
          }

          // use the cookie that we have to pull a csrf token from the config endpoint
          const options = {
            url: configEndpoint,
            headers: {
              "X-Cookie": wrappedCookies
            }
          }

          const response = await CapacitorHttp.get(options);
          const { data: { csrf_token } } = response;
          // TODO: validate the session data
          parsedSessionData.wrappedCookies = wrappedCookies;
          parsedSessionData.csrfToken = csrf_token;
          setLoginData(parsedSessionData);
          
        } catch (e) {
          alert("Failed to validate session data. Please log in again. Error: " + e);
          localStorage.removeItem("loginData");
          localStorage.removeItem("loginTime");
          localStorage.removeItem("loginHeaders");
        }
        setIsLoading(false); // either way, we're done loading
      })();
    }
  }, []);

  if (isLoading) {
    return <IonApp>
      <IonRouterOutlet>
        <IonSpinner className="spinner" name="crescent" />
      </IonRouterOutlet>
    </IonApp>;
  }

  const mainView = loginData.uid >= 0 ? (<IonSplitPane contentId="main">
      <Menu loginData={loginData} />
      <IonRouterOutlet id="main">
      <Route path="/login" exact={true}>
        <Redirect to="/" />
      </Route>
        <Route path="/" exact={true}>
          <Redirect to="/page/about" />
        </Route>
        <Route path="/page/:name" exact={true}>
          <Page loginData={loginData} />
        </Route>
      </IonRouterOutlet>
    </IonSplitPane>) : <IonRouterOutlet id="main">
      <Switch>
        <Route path="/login" exact={true}>
          <Login />
        </Route>
        <Route path="*">
          <Redirect to="/login" />
        </Route>
      </Switch>
    </IonRouterOutlet>;

  return (
    <IonApp>
      <IonReactRouter>
        {mainView}
      </IonReactRouter>
    </IonApp>
  );
};

export default App;
